import { Injectable } from '@angular/core';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CustomHttpErrorResponse, Error } from '../../../../common/constants/error';
import { NotificationService } from '../../../../common/services/notification/notification.service';
import { NfListLoad, NfListSetFilter, NfListSetInitialState } from './nf-list.actions';
import { NfListService } from './nf-list.service';
import { NfSearchFilter, NF_LIST_DEFAULT_PAGE_SIZE } from './models/nf-search.model';
import { NfSearchResponse } from './models/nf-summary.model';
import { NfSummaryResponse } from './models/nf-summary.model';

export interface NfListStateModel {
  items: NfSummaryResponse[];
  pageNumber: number;
  totalPages: number;
  totalElements: number;
  size: number;
  loaded: boolean;
  filter: NfSearchFilter;
}

const initialState: NfListStateModel = {
  items: [],
  pageNumber: 0,
  totalPages: 0,
  totalElements: 0,
  size: NF_LIST_DEFAULT_PAGE_SIZE,
  loaded: false,
  filter: {
    statuses: [],
    searchText: null,
    createdOnStart: null,
    createdOnEnd: null,
  },
};

@State<NfListStateModel>({
  name: 'nfList',
  defaults: initialState,
})
@Injectable()
export class NfListState {
  @Selector()
  static getState(state: NfListStateModel): NfListStateModel {
    return state;
  }

  @Selector()
  static getItems(state: NfListStateModel): NfSummaryResponse[] {
    return state.items;
  }

  @Selector()
  static getTotalElements(state: NfListStateModel): number {
    return state.totalElements;
  }

  @Selector()
  static getLoaded(state: NfListStateModel): boolean {
    return state.loaded;
  }

  constructor(private nfListService: NfListService, private notificationService: NotificationService) {}

  @Action(NfListLoad)
  load(
    ctx: StateContext<NfListStateModel>,
    { payload }: NfListLoad,
  ): Observable<void | MatSnackBarRef<SimpleSnackBar>> {
    ctx.patchState({ loaded: false });
    return this.nfListService.search(payload).pipe(
      map((res: NfSearchResponse) => {
        ctx.patchState({
          items: res.items,
          pageNumber: res.pageNumber,
          totalPages: res.totalPages,
          totalElements: res.totalElements,
          loaded: true,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(NfListSetFilter)
  setFilter(ctx: StateContext<NfListStateModel>, { filter }: NfListSetFilter): void {
    ctx.patchState({ filter: { ...ctx.getState().filter, ...filter } });
  }

  @Action(NfListSetInitialState)
  setInitialState(ctx: StateContext<NfListStateModel>): void {
    ctx.setState(initialState);
  }
}
